/**
 * 篇章列表抽屉（旧 components/EpisodesDrawer.vue）：只请求当前实例的篇章，可查看内容、删除篇章
 */
import { useEffect } from 'react'
import { App, Badge, Button, Drawer, Popconfirm, Space, Table, Typography, type TableColumnsType } from 'antd'
import { episodeListLabel, episodeStatusBadge, formatDate, formatReadingTime } from './constants'
import { useDeleteEpisode, useInstanceEpisodes } from './hooks'
import type { InstanceEpisode } from './types'
import styles from './UserInstances.module.css'

interface EpisodesDrawerProps {
  // 为 null 时抽屉关闭
  instanceId: number | null
  onClose: () => void
  onViewContent: (episode: InstanceEpisode) => void
}

export function EpisodesDrawer({ instanceId, onClose, onViewContent }: Readonly<EpisodesDrawerProps>) {
  const { message } = App.useApp()
  const { data: episodes, isFetching, isError } = useInstanceEpisodes(instanceId)
  const deleteEpisode = useDeleteEpisode()

  useEffect(() => {
    if (isError) message.error('获取篇章列表失败')
  }, [isError, message])

  const remove = async (episode: InstanceEpisode) => {
    if (instanceId === null) {
      message.error('无法获取实例ID')
      return
    }
    try {
      await deleteEpisode.mutateAsync({ instanceId, episodeId: episode.id })
      message.success('篇章删除成功')
    } catch (error) {
      console.error(error)
      message.error('删除篇章失败')
    }
  }

  const columns: TableColumnsType<InstanceEpisode> = [
    {
      title: '篇章',
      key: 'title',
      width: 200,
      render: (_, record) => (
        <div>
          <strong>{record.title || `第${record.episodeNumber}篇章`}</strong>
          <Typography.Text type="secondary" className={styles.subText}>
            Episode {record.episodeNumber}
          </Typography.Text>
        </div>
      )
    },
    {
      title: '完成状态',
      key: 'completion',
      width: 120,
      render: (_, record) => (
        <div>
          <Badge status={episodeStatusBadge(record.status)} text={episodeListLabel(record.status)} />
          {record.episodeReadingStats?.completed_at && (
            <Typography.Text type="secondary" className={styles.subText}>
              {formatDate(record.episodeReadingStats.completed_at)}
            </Typography.Text>
          )}
        </div>
      )
    },
    {
      title: '词汇统计',
      key: 'wordStats',
      width: 120,
      render: (_, record) => (
        <div>
          <div>总词数: {record.episodeReadingStats?.total_words || 0}</div>
          <div>生词数: {record.episodeReadingStats?.unknown_count || 0}</div>
        </div>
      )
    },
    {
      title: '阅读时间',
      key: 'readingTime',
      width: 100,
      render: (_, record) => formatReadingTime(record.episodeReadingStats?.reading_time_seconds)
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => onViewContent(record)}>
            查看内容
          </Button>
          <Popconfirm title="确定要删除这个篇章吗？" okText="确定" cancelText="取消" onConfirm={() => remove(record)}>
            <Button type="link" size="small" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <Drawer open={instanceId !== null} title="篇章列表" size={900} footer={null} onClose={onClose}>
      <Table<InstanceEpisode>
        rowKey="id"
        columns={columns}
        dataSource={episodes}
        pagination={false}
        loading={isFetching}
      />
    </Drawer>
  )
}
